import { useActiveFleet } from '@/data/fleets';
import { isTenantAdminHost } from '@/config';
import { cn } from '@/lib/cn';

const CENTRAL_APP_URL = 'https://app.breakernetwork.app';

/**
 * Native <select> for now. If the user is in a single fleet, render as a
 * static label (no dropdown). For 2+ fleets, render the chooser.
 *
 * FAD A3: on a tenant admin host the Host pins the fleet, so choosing
 * another fleet REDIRECTS to that fleet's own admin address (or central) —
 * never a silent in-place tenant swap. Sessions are per-origin, so the
 * other side asks for a fresh sign-in; accepted v1.
 */
export function FleetSwitcher() {
  const { fleet, fleets, isLoading, setActive } = useActiveFleet();
  const tenantPinned = isTenantAdminHost();

  function choose(id: string) {
    if (!tenantPinned) {
      setActive(id);
      return;
    }
    if (id === fleet?.id) return;
    const target = fleets.find((f) => f.fleet.id === id)?.adminUrl ?? CENTRAL_APP_URL;
    window.location.assign(target);
  }

  if (isLoading) {
    return (
      <span className="text-xs text-breaker-mute font-medium">Loading…</span>
    );
  }
  if (!fleet) {
    return null;
  }
  if (fleets.length === 1) {
    return (
      <span className="text-xs font-medium text-breaker-ink-2 max-w-[180px] truncate">
        {fleet.legalName}
      </span>
    );
  }

  return (
    <label className="relative">
      <span className="sr-only">Active fleet</span>
      <select
        value={fleet.id}
        onChange={(e) => choose(e.target.value)}
        className={cn(
          'appearance-none rounded-md bg-white border border-breaker-line-2',
          'pl-3 pr-8 h-8 text-xs font-medium text-breaker-ink-2 max-w-[200px] truncate',
          'hover:bg-breaker-ground-2 transition-colors cursor-pointer',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breaker-identity focus-visible:ring-offset-2 focus-visible:ring-offset-breaker-ground',
        )}
      >
        {fleets.map(({ fleet: f }) => (
          <option key={f.id} value={f.id}>
            {f.legalName}
          </option>
        ))}
      </select>
      <svg
        aria-hidden
        viewBox="0 0 12 12"
        className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-breaker-mute pointer-events-none"
      >
        <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </label>
  );
}
